import React, { useEffect, useState } from "react";
import { Table, Tag, Button } from "antd";
import { collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import { db } from "../../firebaseConfig";

const panels: any = {
  "panel-1": "Panel 1: Repositioning the Middle East",
  "panel-2": "Panel 2: Exceptional Middle East Location",
  "panel-3": "Panel 3: Bond Trading & Market Structure",
  "panel-4": "Panel 4: Role of Repo in Market Dev",
  "panel-5": "Panel 5: Future of Technology",
  "panel-6": "Panel 6: Future Global Currency System",
};

const PanelSurveyDashboard: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchResponses = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "panel_surveys"));
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setData(rows);
    setLoading(false);
  };

  useEffect(() => {
    fetchResponses();
  }, []);

  /** EXPORT TO EXCEL **/
  const exportToExcel = () => {
    const exportData = data.map((item) => ({
      Name: item.name,
      Email: item.email,
      Organization: item.organization,
      Rating: item.rating,
      Comment: item.comment,
      Panel: item.panelTopic,
      Timestamp: item.timestamp,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Responses");
    XLSX.writeFile(wb, "panel_survey_responses.xlsx");
  };

  /** EXPORT TO EXCEL — 6 PANEL SHEETS (SAFE) **/
  const exportToExcelBySheet = () => {
    // Group responses per panel
    const grouped: any = {
      "panel-1": [],
      "panel-2": [],
      "panel-3": [],
      "panel-4": [],
      "panel-5": [],
      "panel-6": [],
    };

    data.forEach((item) => {
      if (grouped[item.panelId]) {
        grouped[item.panelId].push({
          Name: item.name ?? "",
          Email: item.email ?? "",
          Organization: item.organization ?? "",
          Rating: item.rating ?? "",
          Comment: item.comment ?? "",
          Timestamp: item.timestamp ?? "",
        });
      }
    });

    const wb = XLSX.utils.book_new();

    // Function to sanitize sheet names
    const sanitizeSheetName = (name: string) => {
      // Remove invalid characters
      let safe = name.replace(/[\\/?*[\]:]/g, "");

      // Trim to Excel's 31-character limit
      safe = safe.substring(0, 31);

      // Fallback if empty
      if (!safe.trim()) safe = "Sheet";

      return safe;
    };

    // Add 6 sheets
    Object.keys(grouped).forEach((panelId) => {
      const rows = grouped[panelId];
      const sheetData =
        rows.length > 0 ? rows : [{ Note: "No responses for this panel" }];

      const ws = XLSX.utils.json_to_sheet(sheetData);

      // SAFE SHEET NAME
      const panelTitle = panels[panelId] || panelId;
      const safeSheetName = sanitizeSheetName(panelTitle);

      XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
    });

    XLSX.writeFile(wb, "ICA_Panel_Survey_Responses.xlsx");
  };

  /** PER-PANEL SUMMARY CALCULATION **/
  const getPanelStats = (panelId: string) => {
    const filtered = data.filter((d) => d.panelId === panelId);
    const total = filtered.length;

    const avg =
      total === 0
        ? 0
        : (
            filtered.reduce((s, r) => s + Number(r.rating || 0), 0) / total
          ).toFixed(2);

    const breakdown: any = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    filtered.forEach((f) => (breakdown[f.rating] += 1));

    const commentsCount = filtered.filter(
      (f) => f.comment && f.comment.trim() !== ""
    ).length;

    return { total, avg, breakdown, commentsCount };
  };

  /** TABLE COLUMNS **/
  const columns = [
    { title: "Name", dataIndex: "name", width: 180 },
    { title: "Organization", dataIndex: "organization", width: 220 },
    {
      title: "Rating",
      dataIndex: "rating",
      width: 70,
      render: (r: any) => (
        <span
          style={{
            background: "#f1f1f1",
            padding: "4px 10px",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {r}
        </span>
      ),
    },
    {
      title: "Comments",
      dataIndex: "comment",
      width: 300,
      ellipsis: true,
      render: (c: any) => (c ? c : <span style={{ opacity: 0.5 }}>—</span>),
    },
    { title: "Panel", dataIndex: "panelTopic", width: 280 },
  ];

  /** LIST OF USERS WHO ANSWERED 5 OF 6 SESSIONS **/
  const getFiveOfSixParticipants = () => {
    const tracker: any = {};

    data.forEach((item) => {
      const email = item.email?.trim().toLowerCase();
      if (!email) return;

      if (!tracker[email]) {
        tracker[email] = {
          name: item.name || "",
          email: item.email,
          organization: item.organization,
          panels: new Set(),
        };
      }

      tracker[email].panels.add(item.panelId);
    });

    const result = Object.values(tracker)
      .filter((u: any) => u.panels.size >= 5)
      .filter((u: any) => !u.name.toLowerCase().includes("borham")) // 🚫 EXCLUDE BORHAM
      .map((u: any) => ({
        Name: u.name,
        Email: u.email,
        Organization: u.organization,
        SessionsAnswered: u.panels.size,
      }));

    return result;
  };

  return (
    <div style={{ padding: 30 }}>
      {/* TOP BAR */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 25,
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 600 }}>Panel Survey Dashboard</h2>

        <div style={{ gap: 6 }}>
          <Button type="primary" onClick={exportToExcel}>
            Export to Excel (All)
          </Button>{" "}
          <Button type="primary" onClick={exportToExcelBySheet}>
            Export to Excel (Per Panel)
          </Button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "65% 35%",
          gap: "25px",
        }}
      >
        {/* TABLE */}
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 20,
            border: "1px solid #e6e6e6",
          }}
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={{ pageSize: 15 }}
          />
        </div>

        {/* PEOPLE WHO ANSWERED 5 OF 6 SESSIONS */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #e6e6e6",
            borderRadius: 12,
            padding: "18px 20px",
            marginBottom: 20,
          }}
        >
          <h4
            style={{
              margin: 0,
              marginBottom: 10,
              fontWeight: 600,
              fontSize: 15,
              color: "#222",
            }}
          >
            Participants Who Answered 5 of 6 Sessions
          </h4>

          {getFiveOfSixParticipants().length === 0 ? (
            <p style={{ color: "#666", margin: 0 }}>
              No participants have answered 5 sessions yet.
            </p>
          ) : (
            getFiveOfSixParticipants().map((u, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  color: "#333",
                }}
              >
                <span>
                  <span>{i + 1}.</span> {u.Name}
                </span>

                <span style={{ opacity: 0.7 }}>{u.Organization}</span>
              </div>
            ))
          )}
        </div>

        {/* SUMMARY PER PANEL – 2 COLUMNS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            height: "fit-content",
          }}
        >
          {Object.keys(panels).map((panelId: any) => {
            const stats = getPanelStats(panelId);

            return (
              <div
                key={panelId}
                style={{
                  background: "#fff",
                  border: "1px solid #e6e6e6",
                  borderRadius: 12,
                  padding: "18px 20px",
                  color: "#333",
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    marginBottom: 10,
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#222",
                  }}
                >
                  {panels[panelId]}
                </h4>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                    color: "#444",
                  }}
                >
                  <span>Respondents:</span>
                  <strong style={{ color: "#000" }}>{stats.total}</strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 8,
                    color: "#444",
                  }}
                >
                  <span>Average Rating:</span>
                  <strong style={{ color: "#000" }}>{stats.avg}</strong>
                </div>

                <div style={{ marginTop: 10, marginBottom: 10 }}>
                  {([5, 4, 3, 2, 1] as const).map((r) => (
                    <div
                      key={r}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 6,
                        color: "#444",
                      }}
                    >
                      <span
                        style={{
                          padding: "3px 10px",
                          background: "#f2f2f2",
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 12,
                          color: "#333",
                        }}
                      >
                        {r}
                      </span>

                      <span style={{ color: "#000" }}>
                        {stats.breakdown[r]}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 10,
                    color: "#444",
                  }}
                >
                  <span>Comments:</span>
                  <strong style={{ color: "#000" }}>
                    {stats.commentsCount}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PanelSurveyDashboard;
